// ── Lighthouse cluster (marketplace popup for lighthouse map objects) ──
// Extracted verbatim from MapCanvas.tsx. The ten `lighthouse*` useState pairs,
// the "load listing + rate whenever the popup opens" effect and the five
// handlers (`refetchLighthouse`, buy / list / cancel-listing / wallet-buy) were
// used ONLY by each other + MapModals, so the whole cluster moved here together
// with its two module-level fetch helpers (`fetchListingForHouse`,
// `fetchMarketplaceRate`), which had no other consumer in the codebase.
//
// Bodies are byte-identical to the originals; the only change is that closure
// dependencies (queryClient, `t`, the useTonConnect handles) are parameters
// under their ORIGINAL names.
import { useEffect, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import type { MapObject } from '../../models/mapObject';
import type { MarketplaceListing, MarketplaceRate } from '../../models/apiTypes';
import {
  cancelListingMarketplaceCancelPost,
  createInvoiceTelegramCreateInvoicePost,
  listHouseMarketplaceListPost,
  getListingForHouseMarketplaceListingMapObjectIdGet,
  getRateMarketplaceRateGet,
} from '../../client';
import { handleApiError } from '../../utils/net/apiError';
import { showAlert, showConfirm } from '../../utils/telegram/alert';

async function fetchListingForHouse(mapObjectId: number): Promise<MarketplaceListing | null> {
  const { data, response } = await getListingForHouseMarketplaceListingMapObjectIdGet({
    path: { map_object_id: mapObjectId },
    throwOnError: false,
  });
  if (response?.status === 200) return data ?? null;
  return null;
}

async function fetchMarketplaceRate(): Promise<MarketplaceRate> {
  const { data } = await getRateMarketplaceRateGet({ throwOnError: true });
  return data;
}

export interface UseLighthouseParams {
  /** `activeModal === 'lighthouse'` — by value, re-read every render. */
  isLighthouseModalOpen: boolean;
  queryClient: QueryClient;
  t: (key: string) => string;
  lhWalletConnected: boolean;
  lhSendTon: (params: { to: string; amount: string; comment: string }) => Promise<void>;
  lhSendUsdt: (params: { to: string; usdtAmount: number; comment: string }) => Promise<void>;
  lhConnectWallet: () => void;
}

export interface UseLighthouseResult {
  lighthouseObject: MapObject | null;
  setLighthouseObject: React.Dispatch<React.SetStateAction<MapObject | null>>;
  lighthouseHintOpen: boolean;
  setLighthouseHintOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lighthouseBuyLoading: boolean;
  lighthouseListing: MarketplaceListing | null;
  lighthouseRate: MarketplaceRate | null;
  lighthouseSellOpen: boolean;
  setLighthouseSellOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lighthouseSellPrice: string;
  setLighthouseSellPrice: React.Dispatch<React.SetStateAction<string>>;
  lighthouseSellLoading: boolean;
  lighthouseBuyCurrency: 'ton' | 'usdt';
  setLighthouseBuyCurrency: React.Dispatch<React.SetStateAction<'ton' | 'usdt'>>;
  lighthouseBuyWalletLoading: boolean;
  handleLighthouseBuy: () => Promise<void>;
  handleLighthouseList: () => Promise<void>;
  handleLighthouseCancelListing: () => Promise<void>;
  handleLighthouseWalletBuy: () => Promise<void>;
}

export function useLighthouse({
  isLighthouseModalOpen,
  queryClient,
  t,
  lhWalletConnected,
  lhSendTon,
  lhSendUsdt,
  lhConnectWallet,
}: UseLighthouseParams): UseLighthouseResult {
  const [lighthouseObject, setLighthouseObject] = useState<MapObject | null>(null);
  const [lighthouseHintOpen, setLighthouseHintOpen] = useState(false);
  const [lighthouseBuyLoading, setLighthouseBuyLoading] = useState(false);
  const [lighthouseListing, setLighthouseListing] = useState<MarketplaceListing | null>(null);
  const [lighthouseRate, setLighthouseRate] = useState<MarketplaceRate | null>(null);
  const [lighthouseSellOpen, setLighthouseSellOpen] = useState(false);
  const [lighthouseSellPrice, setLighthouseSellPrice] = useState('');
  const [lighthouseSellLoading, setLighthouseSellLoading] = useState(false);
  const [lighthouseBuyCurrency, setLighthouseBuyCurrency] = useState<'ton' | 'usdt'>('ton');
  const [lighthouseBuyWalletLoading, setLighthouseBuyWalletLoading] = useState(false);

  // (b) Clear listing when popup closes / object cleared — during render
  const listingActive = isLighthouseModalOpen && !!lighthouseObject;
  const [prevListingActive, setPrevListingActive] = useState(listingActive);
  if (listingActive !== prevListingActive) {
    setPrevListingActive(listingActive);
    if (!listingActive) setLighthouseListing(null);
  }

  // Load marketplace listing + rate whenever the lighthouse popup opens (async only)
  useEffect(() => {
    if (!isLighthouseModalOpen || !lighthouseObject) return;
    let cancelled = false;
    (async () => {
      try {
        const [listing, rate] = await Promise.all([
          fetchListingForHouse(lighthouseObject.id).catch(() => null),
          fetchMarketplaceRate().catch(() => null),
        ]);
        if (cancelled) return;
        setLighthouseListing(listing);
        setLighthouseRate(rate);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [isLighthouseModalOpen, lighthouseObject]);

  const refetchLighthouse = async () => {
    await queryClient.invalidateQueries({ queryKey: ['mapObjects'] });
    const fresh = queryClient.getQueryData<MapObject[]>(['mapObjects']);
    const updated = fresh?.find((o) => o.id === lighthouseObject?.id) ?? null;
    if (updated) setLighthouseObject(updated);
    if (lighthouseObject) {
      fetchListingForHouse(lighthouseObject.id).then((l) => setLighthouseListing(l)).catch(() => {});
    }
  };

  const handleLighthouseBuy = async () => {
    if (!lighthouseObject || lighthouseBuyLoading) return;
    setLighthouseBuyLoading(true);
    posthog.capture('lighthouse_buy_clicked', { map_object_id: lighthouseObject.id });
    try {
      const { data } = await createInvoiceTelegramCreateInvoicePost({
        body: {
          product_type: 'lighthouse',
          map_object_id: lighthouseObject.id,
        },
        throwOnError: true,
      });
      const tg = window.Telegram?.WebApp;
      if (!tg?.openInvoice) {
        showAlert(t('Telegram WebApp is not available'));
        return;
      }
      tg.openInvoice(data.invoice_link, (status: string) => {
        if (status === 'paid') {
          posthog.capture('lighthouse_purchased', { map_object_id: lighthouseObject.id });
          refetchLighthouse();
        }
      });
    } catch (e: unknown) {
      const err = handleApiError(e, { logLabel: 'lighthouseCreateInvoice' });
      showAlert(err.detail || t('Failed to create invoice'));
    } finally {
      setLighthouseBuyLoading(false);
    }
  };

  const handleLighthouseList = async () => {
    if (!lighthouseObject || lighthouseSellLoading) return;
    const price = parseFloat(lighthouseSellPrice);
    if (isNaN(price) || price <= 0) {
      showAlert(t('Enter a valid price'));
      return;
    }
    setLighthouseSellLoading(true);
    try {
      const { data: listing } = await listHouseMarketplaceListPost({
        body: { map_object_id: lighthouseObject.id, price_usdt: price },
        throwOnError: true,
      });
      setLighthouseListing(listing);
      setLighthouseSellPrice('');
      setLighthouseSellOpen(false);
      queryClient.invalidateQueries({ queryKey: ['marketplaceListings'] });
      showAlert(t('Lighthouse listed for sale!'));
    } catch (e: unknown) {
      const err = handleApiError(e, { logLabel: 'lighthouseListForSale' });
      showAlert(err.detail || t('Failed to list'));
    } finally {
      setLighthouseSellLoading(false);
    }
  };

  const handleLighthouseCancelListing = async () => {
    if (!lighthouseListing || lighthouseSellLoading) return;
    const confirmed = await showConfirm(t('Cancel this listing?'));
    if (!confirmed) return;
    setLighthouseSellLoading(true);
    try {
      await cancelListingMarketplaceCancelPost({
        body: { listing_id: lighthouseListing.id },
        throwOnError: true,
      });
      setLighthouseListing(null);
      queryClient.invalidateQueries({ queryKey: ['marketplaceListings'] });
      showAlert(t('Listing cancelled'));
    } catch (e: unknown) {
      const err = handleApiError(e, { logLabel: 'lighthouseCancelListing' });
      showAlert(err.detail || t('Failed to cancel listing'));
    } finally {
      setLighthouseSellLoading(false);
    }
  };

  const handleLighthouseWalletBuy = async () => {
    if (!lighthouseListing || !lighthouseRate || lighthouseBuyWalletLoading) return;
    if (!lhWalletConnected) {
      lhConnectWallet();
      return;
    }
    setLighthouseBuyWalletLoading(true);
    try {
      if (lighthouseBuyCurrency === 'ton') {
        await lhSendTon({
          to: lighthouseRate.wallet_address,
          amount: lighthouseListing.price_ton.toFixed(4),
          comment: lighthouseListing.memo,
        });
      } else {
        await lhSendUsdt({
          to: lighthouseRate.wallet_address,
          usdtAmount: lighthouseListing.price_usdt,
          comment: lighthouseListing.memo,
        });
      }
      showAlert(t('Transaction sent! Ownership will transfer after confirmation.'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (!(msg.includes('Interrupted') || msg.includes('Cancelled'))) {
        showAlert(t('Transaction failed. Please try again.'));
      }
    } finally {
      setLighthouseBuyWalletLoading(false);
    }
  };

  return {
    lighthouseObject,
    setLighthouseObject,
    lighthouseHintOpen,
    setLighthouseHintOpen,
    lighthouseBuyLoading,
    lighthouseListing,
    lighthouseRate,
    lighthouseSellOpen,
    setLighthouseSellOpen,
    lighthouseSellPrice,
    setLighthouseSellPrice,
    lighthouseSellLoading,
    lighthouseBuyCurrency,
    setLighthouseBuyCurrency,
    lighthouseBuyWalletLoading,
    handleLighthouseBuy,
    handleLighthouseList,
    handleLighthouseCancelListing,
    handleLighthouseWalletBuy,
  };
}
